/**
 * CO2-Kostenaufteilung nach CO2KostAufG.
 *
 * Rechtsstand geprüft 13.09.2026. Das Gebäudemodernisierungsgesetz (GModG, verkündet
 * 28.07.2026) hat das CO2KostAufG erweitert — es hat das Stufenmodell NICHT abgeschafft:
 *
 *  A) BESTANDSHEIZUNG (Wohngebäude): weiterhin 10-Stufenmodell nach dem Emissions-
 *     kennwert des Gebäudes (§ 5 CO2KostAufG). Gilt unbefristet.
 *
 *  B) NEU EINGEBAUTE fossile Heizung (Gas, Heizöl, Flüssiggas): paritätische Teilung.
 *     - ab 01.01.2028: CO2-Kosten und Gasnetzentgelte je zur Hälfte
 *     - ab 01.01.2029: zusätzlich die Mehrkosten des Bioanteils im Brennstoff
 *       („Bio-Treppe", bis max. 30 % Bioanteil) je zur Hälfte
 *
 * OFFEN — vor Produktivbetrieb am Gesetzeswortlaut prüfen (docs/pruefkatalog-recherche.md):
 *  - der exakte Stichtag, ab dem ein Heizungseinbau als „neu" im Sinne von B) gilt
 *  - die Ausnahme- und Härtefalltatbestände (u. a. sehr geringe Mieteinnahmen)
 *  - Nichtwohngebäude: bis 2025 pauschal 50/50, ab 2026 Übergang ins Stufenmodell
 *
 * Deshalb entscheidet NIE das Kalenderjahr allein über das Modell, sondern
 * `modellFuer()` aus Jahr + Anlagenzustand. Altjahre bleiben nachrechenbar.
 */

import { Cent, teileProzent } from './geld';

export type Co2Modell = 'stufen_10' | 'fix_50_50';

export type Brennstoff =
  | 'gas'
  | 'heizoel'
  | 'fluessiggas'
  | 'fernwaerme'
  | 'waermepumpe'
  | 'holz'
  | 'sonstige';

export const FOSSILE_BRENNSTOFFE: readonly Brennstoff[] = ['gas', 'heizoel', 'fluessiggas'];

export interface Co2Stufe {
  stufe: number;
  /** untere Grenze inklusive, in kg CO2 / m² / a */
  von: number;
  /** obere Grenze exklusive; null = offen nach oben */
  bis: number | null;
  vermieterProzent: number;
  mieterProzent: number;
}

/** § 5 CO2KostAufG — Wohngebäude mit Bestandsheizung. */
export const STUFEN_WOHNGEBAEUDE: readonly Co2Stufe[] = [
  { stufe: 1,  von: 0,  bis: 12,   vermieterProzent: 0,  mieterProzent: 100 },
  { stufe: 2,  von: 12, bis: 17,   vermieterProzent: 10, mieterProzent: 90 },
  { stufe: 3,  von: 17, bis: 22,   vermieterProzent: 20, mieterProzent: 80 },
  { stufe: 4,  von: 22, bis: 27,   vermieterProzent: 30, mieterProzent: 70 },
  { stufe: 5,  von: 27, bis: 32,   vermieterProzent: 40, mieterProzent: 60 },
  { stufe: 6,  von: 32, bis: 37,   vermieterProzent: 50, mieterProzent: 50 },
  { stufe: 7,  von: 37, bis: 42,   vermieterProzent: 60, mieterProzent: 40 },
  { stufe: 8,  von: 42, bis: 47,   vermieterProzent: 70, mieterProzent: 30 },
  { stufe: 9,  von: 47, bis: 52,   vermieterProzent: 80, mieterProzent: 20 },
  { stufe: 10, von: 52, bis: null, vermieterProzent: 95, mieterProzent: 5 },
] as const;

/** Erstes Abrechnungsjahr der paritätischen Teilung bei neu eingebauter fossiler Heizung. */
export const PARITAET_AB_JAHR = 2028;
/** Ab diesem Abrechnungsjahr werden zusätzlich die Bioanteil-Mehrkosten hälftig geteilt. */
export const BIOANTEIL_HAELFTIG_AB_JAHR = 2029;

export interface AnlagenZustand {
  brennstoff: Brennstoff;
  /**
   * true, wenn die Heizungsanlage als „neu eingebaut" im Sinne des durch das GModG
   * geänderten CO2KostAufG gilt. Bewusst ein Flag am Objekt und keine Ableitung aus
   * einem im Code hinterlegten Stichtag — der Stichtag ist noch zu verifizieren.
   */
  neuEingebautNachGModG: boolean;
}

export function modellFuer(abrechnungsjahr: number, anlage?: AnlagenZustand): Co2Modell {
  if (!anlage) return 'stufen_10';
  const fossil = FOSSILE_BRENNSTOFFE.includes(anlage.brennstoff);
  if (abrechnungsjahr >= PARITAET_AB_JAHR && fossil && anlage.neuEingebautNachGModG) {
    return 'fix_50_50';
  }
  return 'stufen_10';
}

export function stufeFuerKennwert(kgProQmProJahr: number): Co2Stufe {
  if (kgProQmProJahr < 0 || !Number.isFinite(kgProQmProJahr)) {
    throw new Error(`Ungültiger CO2-Kennwert: ${kgProQmProJahr}`);
  }
  const treffer = STUFEN_WOHNGEBAEUDE.find(
    (s) => kgProQmProJahr >= s.von && (s.bis === null || kgProQmProJahr < s.bis),
  );
  if (!treffer) throw new Error(`Keine CO2-Stufe für Kennwert ${kgProQmProJahr}`);
  return treffer;
}

/**
 * Gebäude-Emissionskennwert: kg CO2 pro m² Wohnfläche und Jahr.
 * Abrechnungszeiträume ungleich 365 Tagen werden auf ein Jahr hochgerechnet.
 */
export function emissionsKennwert(args: {
  co2MengeKg: number;
  wohnflaecheQm: number;
  tageImZeitraum?: number;
}): number {
  const { co2MengeKg, wohnflaecheQm, tageImZeitraum = 365 } = args;
  if (wohnflaecheQm <= 0) throw new Error('Wohnfläche muss > 0 sein');
  if (tageImZeitraum <= 0) throw new Error('Zeitraum muss > 0 Tage haben');
  return (co2MengeKg / wohnflaecheQm) * (365 / tageImZeitraum);
}

export interface Co2Aufteilung {
  modell: Co2Modell;
  /** null beim paritätischen Modell */
  stufe: number | null;
  kennwert: number | null;
  vermieterProzent: number;
  mieterProzent: number;
  vermieterAnteilCent: Cent;
  mieterAnteilCent: Cent;
  /** Hälftige Netzentgelte / Bioanteil-Mehrkosten, sofern übergeben. */
  netzentgeltVermieterCent: Cent;
  bioanteilVermieterCent: Cent;
  hinweise: string[];
}

export interface Co2Eingabe {
  co2KostenCent: Cent;
  abrechnungsjahr: number;
  /** nur für das Stufenmodell nötig */
  kennwert?: number;
  anlage?: AnlagenZustand;
  /** Gasnetzentgelte im Zeitraum — ab 2028 bei neuer fossiler Heizung hälftig. */
  netzentgelteCent?: Cent;
  /** Mehrkosten des Bioanteils — ab 2029 bei neuer fossiler Heizung hälftig. */
  bioanteilMehrkostenCent?: Cent;
}

/**
 * Teilt die CO2-Kosten eines Objekts zwischen Vermieter und Mieterschaft auf.
 * Der Vermieteranteil mindert die auf die Mieter umlegbaren Heizkosten.
 */
export function teileCo2Kosten(eingabe: Co2Eingabe): Co2Aufteilung {
  const {
    co2KostenCent,
    abrechnungsjahr,
    kennwert,
    anlage,
    netzentgelteCent = 0,
    bioanteilMehrkostenCent = 0,
  } = eingabe;

  const modell = modellFuer(abrechnungsjahr, anlage);
  const hinweise: string[] = [];

  let vermieterProzent: number;
  let stufe: number | null = null;

  if (modell === 'fix_50_50') {
    vermieterProzent = 50;
    hinweise.push(
      'Paritätische Aufteilung nach dem durch das GModG geänderten CO2KostAufG ' +
        '(neu eingebaute fossile Heizungsanlage).',
    );
  } else {
    if (kennwert === undefined) {
      throw new Error(
        `Für Abrechnungsjahr ${abrechnungsjahr} gilt das Stufenmodell — ` +
          'der CO2-Kennwert des Gebäudes ist erforderlich.',
      );
    }
    const s = stufeFuerKennwert(kennwert);
    stufe = s.stufe;
    vermieterProzent = s.vermieterProzent;
  }

  const { a: vermieterAnteilCent, b: mieterAnteilCent } = teileProzent(
    co2KostenCent,
    vermieterProzent,
  );

  // Netzentgelte und Bioanteil-Mehrkosten werden nur im paritätischen Modell geteilt.
  let netzentgeltVermieterCent = 0;
  let bioanteilVermieterCent = 0;

  if (modell === 'fix_50_50' && netzentgelteCent > 0) {
    netzentgeltVermieterCent = teileProzent(netzentgelteCent, 50).a;
  } else if (netzentgelteCent > 0) {
    hinweise.push(
      'Gasnetzentgelte werden nur bei neu eingebauter fossiler Heizung ab 2028 ' +
        'hälftig getragen und bleiben hier vollständig umlagefähig.',
    );
  }

  if (
    modell === 'fix_50_50' &&
    abrechnungsjahr >= BIOANTEIL_HAELFTIG_AB_JAHR &&
    bioanteilMehrkostenCent > 0
  ) {
    bioanteilVermieterCent = teileProzent(bioanteilMehrkostenCent, 50).a;
  } else if (bioanteilMehrkostenCent > 0) {
    hinweise.push(
      `Mehrkosten des Bioanteils werden erst ab Abrechnungsjahr ` +
        `${BIOANTEIL_HAELFTIG_AB_JAHR} hälftig getragen.`,
    );
  }

  return {
    modell,
    stufe,
    kennwert: kennwert ?? null,
    vermieterProzent,
    mieterProzent: 100 - vermieterProzent,
    vermieterAnteilCent,
    mieterAnteilCent,
    netzentgeltVermieterCent,
    bioanteilVermieterCent,
    hinweise,
  };
}
